import { toast } from "$lib/components/organisms/toast/toast";
import { useDataSources } from "$lib/core/map/cesium-datasources";
import { OriginalCesiumInputActions } from "$lib/core/map/cesium-original-input-actions";
import { useCesiumViewer } from "$lib/core/map/cesium-viewer";
import { toggleEscapeToolListener } from "$lib/core/map/map-tools";
import { mapToolsStore } from "$lib/core/map/map-tools-store";
import { EntityTypes, getEntityPropertyBag } from "$lib/core/map/map-utils";
import { getTrenchTopWidth } from "$lib/core/map/trench-utils";
import { type ICableModel } from "$lib/models/ICableModel";
import { Matrix4, Transforms, Math as CesiumMath, Cartesian3, SceneTransforms, PolylineDashMaterialProperty, Color, Entity, JulianDate, Cartographic, defined, Cartesian2, BoundingSphere, ScreenSpaceEventType, ConstantProperty, PositionProperty, ConstantPositionProperty, Property, CallbackProperty, CallbackPositionProperty, ScreenSpaceEventHandler, PropertyBag, CorridorGraphics, CornerType, HeightReference, ColorMaterialProperty } from "cesium";
import { cloneDeep } from "lodash";
import { get } from "svelte/store";

//#region Constants
/********************* PARAMETERS (Tune tolerance for window to world coordinates in pixels) *************************/
const WORLD_TOL_METERS_TERRAIN = 30,
MIN_PX_TOL = 8,
MAX_PX_TOL = 48;

//#region Helpers
/********************************************************/
/*********************** HELPERS ************************/
/********************************************************/


/**
 * Lokal ENU transform ved et bakkepunkt G (Cartesian3 på terreng)
 * @param G Origin which will be origo (0,0,0) in ENU fromFixed and world in toFixed
 * @returns toFixed: Matrix4 (ECEF), fromFixed: Matrix4 (ENU)
 */
const enuTransformsAt = (G: Cartesian3) => {
    // 4x4 transform fra ENU -> ECEF ved G
    const toFixed = Transforms.eastNorthUpToFixedFrame(G);
    // Invers for ECEF -> ENU
    const fromFixed = Matrix4.inverse(toFixed, new Matrix4());
    return { toFixed, fromFixed };
}
  
/**
 * Transform ECEF to ENU
 * @param pointECEF 
 * @param fromFixed 
 * @returns 
 */ 
const ecefToEnu = (pointECEF: Cartesian3, fromFixed: Matrix4) => {
    const v = Matrix4.multiplyByPoint(
        fromFixed,
        pointECEF,
        new Cartesian3()
    );
    return v; // (x=E, y=N, z=U)
}
  
/**
 * Transform ENU to ECEF
 * @param pointENU 
 * @param toFixed 
 * @returns 
 */
const enuToEcef = (pointENU: Cartesian3, toFixed: Matrix4) => {
    return Matrix4.multiplyByPoint(
        toFixed,
        pointENU,
        new Cartesian3()
    );
}
  
/**
 * Get terrain at mouse position
 * @param windowPos 
 * @returns 
 */
const terrainAtMouse = (windowPos: Cartesian2) => {
    const viewer = useCesiumViewer();
    const ray = viewer.camera.getPickRay(windowPos);
    if (ray) {
        const g = viewer.scene.globe.pick(ray, viewer.scene);
        if (g) return g;
    }
    return viewer.camera.pickEllipsoid(windowPos, viewer.scene.globe.ellipsoid);
}
  
/**
 * Find closest point from mouse coordinates (screen) to segment AB in ENU by groundpoint G
 * 
 * @param mousePos in screen coordinates (pixels)
 * @param A_ecef Endpoint A of segment in ECEF
 * @param B_ecef Endpoint B of segment in ECEF
 * @param G_ecef Ground point in ECEF (under mouse, terrain or ellipsoid)
 * @returns 
 */
const closestToSegmentInENU = (mousePos: Cartesian2, A_ecef: Cartesian3, B_ecef: Cartesian3, G_ecef: Cartesian3) => {
    const viewer = useCesiumViewer();
    const { toFixed, fromFixed } = enuTransformsAt(G_ecef);

    // A,B in ENU, local coordinates
    const A_enu = ecefToEnu(A_ecef, fromFixed);
    const B_enu = ecefToEnu(B_ecef, fromFixed);

    // 2D vector-projection in ENU. Ignore Z to match user screen-space and improve mouse to world coordinate comparison and precision
    const Ax = A_enu.x,
        Ay = A_enu.y;
    const Bx = B_enu.x,
        By = B_enu.y;
    const vx = Bx - Ax,
        vy = By - Ay;
    const seg2 = vx * vx + vy * vy;

    let segmentPos = 0;
    if (seg2 > 0) {
        const wx = -Ax,
        wy = -Ay; // P_enu - A_enu (P=(0,0))
        segmentPos = (wx * vx + wy * vy) / seg2;
        segmentPos = Math.max(0, Math.min(1, segmentPos));
    }

    const qx = Ax + segmentPos * vx,
        qy = Ay + segmentPos * vy,
        qz = 0; // nærmeste i ENU-plan
    const closestIntersectionPoint = enuToEcef(new Cartesian3(qx, qy, qz), toFixed);

    // Pikselavstand: projiser Q_ecef til skjerm, sammenlign med mousePos
    const w = SceneTransforms.worldToWindowCoordinates(
        viewer.scene,
        closestIntersectionPoint
    );
    const pixelDistance = w ? Math.hypot(mousePos.x - w.x, mousePos.y - w.y) : Infinity;

    return { segmentPos, closestIntersectionPoint, pixelDistance };
}

const metersPerPixelAtTerrainPoint = (groundPoint: Cartesian3 | null) => {
    const viewer = useCesiumViewer();
    const scene = viewer.scene;
    const globe = scene.globe;
    if (!groundPoint || !defined(globe)) return 1.0;

    const bs = new BoundingSphere(groundPoint, 1.0);
    let mpp = viewer.camera.getPixelSize(
        bs,
        scene.drawingBufferWidth,
        scene.drawingBufferHeight
    );
    if (!defined(mpp) || !isFinite(mpp) || mpp <= 0) {
        const camCarto = viewer.camera.positionCartographic;
        const terrH = globe.getHeight(camCarto);
        const hAGL = (camCarto?.height ?? 1000) - (terrH ?? 0);
        mpp = Math.max(0.1, hAGL / 900.0);
    }
    return mpp;
}

//#region Activate tool
/********************************************************/
/*********************** MAIN TOOL **********************/
/********************************************************/
const activateTrenchSubdivisionTool = (originalPolylines: Entity[], trenchParent: string) => {
    const viewer = useCesiumViewer();
    const {temp_referenceDataSource, trenchEntityDatasource} = useDataSources();
    const COLORS = [Color.YELLOW, Color.CYAN, Color.MAGENTA, Color.ORANGE, Color.LIME];
    toggleEscapeToolListener({toggleOn: true});

    mapToolsStore.update((store) => {
        store.polylineSubdivision.toolEnabled = true; // Activate tool
        store.polylineSubdivision.parentPath = trenchParent; // Parent trench entity id
        store.polylineSubdivision.subdivisions = originalPolylines; // Existing subdivisions on tool activation
        return store;
    })

    let polyEntities = get(mapToolsStore).polylineSubdivision.subdivisions;

    // Interaction object
    let hovered: { entity: Entity | null; seg: number; t: number; pixelDistance: number; closestIntersection: Cartesian3 | null} = { 
        entity: null, 
        seg: -1, 
        t: 0, 
        pixelDistance: Infinity, 
        closestIntersection: null 
    };

    // Temporary preview entities
    const previewPoint = temp_referenceDataSource.entities.add({
        position: Cartesian3.fromDegrees(0, 0),
        point: {
            pixelSize: 10,
            color: Color.YELLOW.withAlpha(0.95),
            outlineColor: Color.BLACK,
            outlineWidth: 2,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        show: false,
    });
    const previewSegment = temp_referenceDataSource.entities.add({
        polyline: {
            positions: [],
            width: 6,
            clampToGround: true,
            material: new PolylineDashMaterialProperty({
                color: Color.RED,
                dashLength: 12,
            })
        },
        show: false,
    });
  
    const hidePreview = () => {
        previewPoint.show = false;
        previewSegment.show = false;
        polyEntities.forEach((e) => (e.polyline!.width = new CallbackProperty(() => 4, false)));
        hovered = { entity: null, seg: -1, t: 0, pixelDistance: Infinity, closestIntersection: null };
      }
  
    // Find closest in screen coordinates, tested against a dynamic threshold 
    viewer.screenSpaceEventHandler.setInputAction((movement: { startPosition: Cartesian2, endPosition: Cartesian2 }) => {
        if (polyEntities.length === 0) return;
  
        const mousePos = movement.endPosition;
        // Terrain @ cursor
        const G = terrainAtMouse(mousePos);
        if (!G) {
            hidePreview();
            return;
        }
  
        let best: { entity: Entity | null; seg: number; t: number; pixelDistance: number; closestIntersection: Cartesian3 | null} = 
            { entity: null, seg: -1, t: 0, pixelDistance: Infinity, closestIntersection: null };
  
        for (const entity of polyEntities) {
            if(!defined(entity.polyline) || !defined(entity.polyline.positions)) continue;
            const pos = entity.polyline.positions.getValue(JulianDate.now());
            if (!pos || pos.length < 2) continue;
    
            // Loop through every segment in polyline
            for (let i = 0; i < pos.length - 1; i++) {
                const A = pos[i],
                    B = pos[i + 1]; 
                //Check current segment for closest point to mouse in ENU.
                const current = closestToSegmentInENU(mousePos, A, B, G);
                if (current.pixelDistance < best.pixelDistance) {
                    best = { 
                        entity, 
                        seg: i, 
                        t: current.segmentPos, 
                        pixelDistance: current.pixelDistance, 
                        closestIntersection: current.closestIntersectionPoint 
                    }
                };
            }
        }
  
        // Pixel treshold relative to _terrain_
        const mpp = metersPerPixelAtTerrainPoint(G);
        const pxTol = CesiumMath.clamp(
            WORLD_TOL_METERS_TERRAIN / mpp,
            MIN_PX_TOL,
            MAX_PX_TOL
        );
  
        if (defined(best.entity?.polyline?.positions) && best.pixelDistance <= pxTol) {
            hovered = best;
            const pos = best.entity.polyline.positions.getValue(
                new JulianDate
            ) as Cartesian3[];

            if(!defined(best.closestIntersection)) return;
            const newPreviewPos = best.closestIntersection;
            previewPoint.position = new CallbackPositionProperty(() => newPreviewPos, false);
            previewPoint.show = true;

            const seg1 = pos[best.seg];
            const seg2 = pos[best.seg + 1];
            previewSegment.polyline!.positions = new CallbackProperty(() => [seg1, seg2], false);
            previewSegment.show = true;
        
            polyEntities.forEach((e) => (e.polyline!.width = new CallbackProperty(() => 4, false)));
            best.entity.polyline.width = new CallbackProperty(() => 6, false);
        } else {
            hidePreview();
        }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  
    // LEFT_CLICK: split clicked polyline. Use threshold on position
    viewer.screenSpaceEventHandler.setInputAction(() => {
        if (!hovered.entity) return;

        // Verify threshold by closestIntersection
        const mpp = metersPerPixelAtTerrainPoint(hovered.closestIntersection);
        const pxTol = CesiumMath.clamp(
            WORLD_TOL_METERS_TERRAIN / mpp,
            MIN_PX_TOL,
            MAX_PX_TOL
        );
        if (hovered.pixelDistance > pxTol) return;
  
        const e = hovered.entity;
        if(!defined(e?.polyline?.positions)) {
            console.error('No positions on entity?');
            return;
        }
        const pos = e.polyline.positions.getValue(new JulianDate);
        const i = hovered.seg;
        const t = hovered.t;
        const Q = hovered.closestIntersection; // ENU-beregnet punkt som matcher pekeren
  
        const left = [];
        for (let k = 0; k <= i; k++) left.push(pos[k]);
        if (t > 1e-9) left.push(Q);
  
        const right = [];
        if (t < 1 - 1e-9) right.push(Q);
        for (let k = i + 1; k < pos.length; k++) right.push(pos[k]);

        const entityProperties = e.properties?.getValue(new JulianDate);
        const entityReferenceId = entityProperties.referenceEntityId;
  
        temp_referenceDataSource.entities.remove(e);
        const idx = polyEntities.indexOf(e);
        if (idx >= 0) polyEntities.splice(idx, 1);

        // Also remove the actual cablepath from the original trench datasource. If split, it should no longer exist.
        // By the way, all polylines should have properties
        if(entityReferenceId){
            const cablePathEntity = trenchEntityDatasource.entities.getById(entityReferenceId);
            if(cablePathEntity){ 
                cablePathEntity.parent = undefined; // Detach from parent trench entity
                trenchEntityDatasource.entities.remove(cablePathEntity);
            }
            
        }

        // Passed entity may contain more than just the typed property bag. Make sure no more is inherited
        const {entityType, data} = getEntityPropertyBag<ICableModel.CablePathPropertyBagModel>(e); // Inherit properties from clicked polyline
        const propertybag = new PropertyBag({
            entityType,
            data
        });

        const screenLeft = temp_referenceDataSource.entities.add({
            polyline: {
                positions: left,
                width: 4,
                clampToGround: true
            },
            properties: new PropertyBag(propertybag)
        });
        const screenRight = temp_referenceDataSource.entities.add({
            polyline: {
                positions: right,
                width: 4,
                clampToGround: true
            },
            properties: new PropertyBag(propertybag)
        });

        mapToolsStore.update((store) => {
            // Closest to "start", assuming screenLeft is closest to start of trench
            store.polylineSubdivision.subdivisions.splice(idx, 0, screenLeft)
            //Closest to "end"
            store.polylineSubdivision.subdivisions.splice(idx + 1, 0, screenRight);
            //store.polylineSubdivision.subdivisions = [...store.polylineSubdivision.subdivisions, screenLeft, screenRight];
            polyEntities = store.polylineSubdivision.subdivisions;
            return store;
        });

        polyEntities.forEach((ent, i) => (ent.polyline!.material = new ColorMaterialProperty(COLORS[i % COLORS.length])));
    
        hidePreview();
        // Remove the split entity. It no longer exists

    }, ScreenSpaceEventType.LEFT_CLICK);

    viewer.screenSpaceEventHandler.setInputAction(() => {
        deactivateTrenchSubdivisionTool();
    }, ScreenSpaceEventType.RIGHT_CLICK);
}

const deactivateTrenchSubdivisionTool = () => {
    toggleEscapeToolListener({toggleOn: false});
    const viewer = useCesiumViewer();
    const {temp_referenceDataSource, trenchEntityDatasource} = useDataSources();
    const { polylineSubdivision } = get(mapToolsStore);
    const subdivisons = polylineSubdivision.subdivisions;
    const trenchEntity = trenchEntityDatasource.entities.getById(polylineSubdivision.parentPath);

    viewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
    viewer.screenSpaceEventHandler.setInputAction(
        OriginalCesiumInputActions.LEFT_CLICK,
        ScreenSpaceEventType.LEFT_CLICK
    );
    // Remove temp entities and reset
    temp_referenceDataSource.entities.removeAll();
    // Reset show state on corridor and update with new subdivisions
    // Todo: When backend handles subdivisions, we repurpose this part into an UPDATE query and process czml
    if(trenchEntity) {
        trenchEntity.show = true;
        subdivisons.forEach((entity) => {
            if(defined(entity?.polyline?.positions)){

                // We create new trench entities from all polylines/subdivision in collection (full trench).
                // If we keep an entity that already exists in the trench datasource, we should skip adding it again.
                // An example is if there is two subdivisions, and the user only split one of them.
                // We know a subdivision hasn't been modified if it has a "referenceEntityId" property which matches an existing entity in the trench datasource
                
                //@ts-ignore -> Local variant of ICableModel.CablePathPropertyBagModel which only exists during tool runtime.
                if(getEntityPropertyBag(entity).referenceEntityId){
                    return;
                }

                const positions = entity.polyline.positions.getValue(new JulianDate);
                const propertybag = getEntityPropertyBag<ICableModel.CablePathPropertyBagModel>(entity);
                const clonedProperties = cloneDeep(propertybag.data);
                const prefix = 'cablePath:';
                const corridorSubdivision = new Entity({
                    id: prefix + entity.id,
                    parent: trenchEntity,
                    corridor: new CorridorGraphics({
                        positions: positions,
                        width: getTrenchTopWidth(propertybag.data),
                        cornerType: CornerType.MITERED,
                        heightReference: HeightReference.CLAMP_TO_GROUND,
                        material: new ColorMaterialProperty(Color.BEIGE),
                        zIndex: 10
                    }),
                    properties: new PropertyBag({
                        entityType: EntityTypes.CABLE_PATH,
                        data: clonedProperties
                    })
                });
                // Todo: 
                // Add properties which inherit from targeted subdivision
                trenchEntityDatasource.entities.add(corridorSubdivision);
            } else {
                console.error('No positions on entity?');
            }
            
        });
    }
    mapToolsStore.update((store) => {
        store.polylineSubdivision.toolEnabled = false;
        store.polylineSubdivision.subdivisions = [];
        store.polylineSubdivision.parentPath = '';
        return store;
    })
}

export { activateTrenchSubdivisionTool, deactivateTrenchSubdivisionTool }